import type { MissionStep } from './missionTypes.js';
import type { LearningLabPlaybook } from './missionTypes.js';
import { moduleDashboardStepTarget } from './dashboardModuleTour.js';

export interface BuiltMissionTourStep {
  stepName: string;
  target: string;
  placement: string;
  title: string;
  content: string;
  tip?: string;
  metadata?: Record<string, unknown>;
}

function missionStepTarget(step: MissionStep): string {
  if (step.placement === 'center' || !step.tourAnchor) {
    return 'body';
  }
  return moduleDashboardStepTarget({
    stepName: step.stepName,
    tourAnchor: step.tourAnchor,
    title: step.title,
    content: step.instruction,
  });
}

export function buildMissionTourSteps(
  playbook: LearningLabPlaybook,
): BuiltMissionTourStep[] {
  return playbook.steps.map((step) => {
    const metadata: Record<string, unknown> = {
      missionStepId: step.id,
      missionGroupId: step.groupId,
      missionKind: step.kind,
      advanceGate: step.advanceGate,
      checklistTitle: step.title,
      instruction: step.instruction,
      why: step.why,
      optional: step.optional ?? false,
      isPanelTarget: step.isPanelTarget ?? false,
      ...(step.metadata ?? {}),
    };

    if (step.module) {
      metadata.module = step.module;
    }
    if (step.coordinatorTabSlug) {
      metadata.coordinatorTabSlug = step.coordinatorTabSlug;
    }
    if (step.beforeAction) {
      metadata.beforeAction = step.beforeAction;
    }
    if (step.targetZone) {
      metadata.targetZone = step.targetZone;
    }
    if (step.scrollPriority) {
      metadata.scrollPriority = step.scrollPriority;
    }
    if (step.panelFallback) {
      metadata.panelFallback = step.panelFallback;
    }
    if (step.tourAnchor) {
      metadata.tourAnchor = step.tourAnchor;
    }

    return {
      stepName: step.stepName,
      target: missionStepTarget(step),
      placement: step.placement ?? (step.tourAnchor ? 'bottom' : 'center'),
      title: step.title,
      content: step.instruction,
      tip: step.tip,
      metadata,
    };
  });
}

export function getPlaybookIncompleteStepTitles(
  playbook: LearningLabPlaybook,
  fromIndex: number,
): string[] {
  return playbook.steps.slice(fromIndex + 1).map((step) => step.title);
}

type TourStepMetadataCarrier = { metadata?: Record<string, unknown> };

export function missionStepIdFromTourStep(
  step: TourStepMetadataCarrier | undefined,
): string | undefined {
  const id = step?.metadata?.missionStepId;
  return typeof id === 'string' ? id : undefined;
}

/** Playbook step index for the tour step at `tourIndex`, or -1 for manifest-only steps (e.g. lab_enter). */
export function missionPlaybookIndexFromTour(
  steps: TourStepMetadataCarrier[],
  tourIndex: number,
  playbook: LearningLabPlaybook,
): number {
  const missionStepId = missionStepIdFromTourStep(steps[tourIndex]);
  if (!missionStepId) {
    return -1;
  }
  return playbook.steps.findIndex((step) => step.id === missionStepId);
}

export function getMissionProgressDisplay(
  steps: TourStepMetadataCarrier[],
  tourIndex: number,
  playbook: LearningLabPlaybook,
): { current: number; total: number } | null {
  const playbookIndex = missionPlaybookIndexFromTour(steps, tourIndex, playbook);
  if (playbookIndex < 0) {
    return null;
  }
  return { current: playbookIndex + 1, total: playbook.steps.length };
}

export function getPlaybookIncompleteStepTitlesFromTourIndex(
  steps: TourStepMetadataCarrier[],
  tourIndex: number,
  playbook: LearningLabPlaybook,
): string[] {
  const playbookIndex = missionPlaybookIndexFromTour(steps, tourIndex, playbook);
  if (playbookIndex < 0) {
    return playbook.steps.map((step) => step.title);
  }
  return getPlaybookIncompleteStepTitles(playbook, playbookIndex);
}
